/**
 * ai-start-page.tsx — Nhân viên AI › "Bắt đầu" (`/ai/bat-dau`): tình trạng THẬT của tổ chức — đã kết nối bao nhiêu kênh,
 * kênh nào đang có nhân viên AI trả lời (từ danh sách nhân viên AI: kênh phụ trách, nhân viên mặc định, `status`) — và
 * danh sách việc tiếp theo có link. Nút chính "Tạo nhân viên AI" → luồng tạo 5 bước `/ai/nhan-vien/moi`; checklist dùng
 * cùng tiêu chí với luồng tạo (wizard-lib.alignStartSteps) và có bản nháp dở thì dẫn vào lại đúng bước.
 * Nguyên tắc điều hướng (open-target-lib): tạo / mở nhân viên AI = cùng tab; màn khác (Kết nối, Kho tri thức, Nguồn AI &
 * Credit, Theo dõi) = TAB MỚI, trang này giữ nguyên + nút "Tải lại" trên từng thẻ.
 */
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Bot, CheckCircle2, Circle, PlugZap, Plus } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { PlatformTag } from '@/components/shared/platform-mark'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useMyPermissions } from '@/hooks/use-settings'
import { aiKeys, useAiBots, useAiConfig } from '@/hooks/use-ai'
import { aiCreditKeys, useAiCredits, useAiSources } from '@/hooks/use-ai-credits'
import { useInboxAccounts, zaloAccountsKey } from '@/hooks/use-integrations'
import { KB_BASES_KEY, useKnowledgeBases } from '@/hooks/use-knowledge-bases'
import { AiFlowLink, OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { CONNECT_CHANNELS_PATH } from '@/components/ai/open-target-lib'
import {
  AI_PATHS, aiSourceReady, botPath, channelCoverage, groupStartSteps, startSteps, type StartStep,
} from '@/lib/ai-khung'
import { alignStartSteps, draftBots, wizardResumePath } from './wizard/wizard-lib'
import { testedWizardBotIds } from './wizard/wizard-storage'
import { useAiPerms } from './use-ai-perms'

/** Dữ liệu của trang (kênh + nhân viên AI + tình trạng nguồn / kho) — nút "Tải lại" của các thẻ. */
const COVERAGE_KEYS = [zaloAccountsKey(undefined, false), aiKeys.bots]
const STEP_KEYS = [...COVERAGE_KEYS, aiKeys.config, aiCreditKeys.sources, aiCreditKeys.credits, KB_BASES_KEY]

export function AiStartPage() {
  const { t } = useTranslation('aiWizard')
  // Tạo nhân viên AI / đổi nguồn / bật trả lời: backend đòi `ai.update`; kết nối kênh: `integrations.create`.
  // Không có quyền → không hiện nút (đang tải quyền = chưa có quyền).
  const { canEdit } = useAiPerms()
  const { data: perms } = useMyPermissions()
  const canConnect = !!perms?.has('integrations.create')
  const navigate = useNavigate()
  const botsQ = useAiBots()
  const channelsQ = useInboxAccounts()
  const { data: config } = useAiConfig()
  const { data: sources } = useAiSources()
  const { data: credits } = useAiCredits()
  const { data: bases } = useKnowledgeBases()

  if (botsQ.isLoading || channelsQ.isLoading) return <Loading label={t('start.checking')} />
  if (botsQ.isError || channelsQ.isError) return <ErrorState message={t('start.loadError')} />

  const bots = botsQ.data?.bots ?? []
  const channels = (channelsQ.data ?? []).filter((c) => !c.isDisabled)
  const paused = config ? !config.autoReplyEnabled || config.enabled === false || !!config.platformAiDisabled : undefined
  const coverage = channelCoverage(channels, bots, { paused: !!paused })
  const answeringCount = coverage.filter((c) => c.answering).length
  const baseSteps = startSteps({
    channels: channels.length,
    bots,
    coverage,
    paused,
    knowledgeReady: bases ? bases.some((b) => b.articleCount + b.qaCount + b.materialCount > 0) : null,
    sourceReady: aiSourceReady(sources, credits),
    canEditSources: canEdit,
  })
  const steps = alignStartSteps(baseSteps, { bots, bases, testedBotIds: testedWizardBotIds() })
  const { next, done, current } = groupStartSteps(steps)
  const draft = canEdit ? draftBots(bots)[0] : undefined

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('start.title')}
        description={t('start.description')}
        actions={canEdit ? (
          <Button onClick={() => navigate(AI_PATHS.newBot)}><Plus className="h-4 w-4" /> {t('start.create')}</Button>
        ) : undefined}
      />

      {draft && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <span className="text-2xl" aria-hidden>{draft.avatarEmoji ?? '🤖'}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('start.draftInProgress', { name: draft.name })}</p>
              <p className="text-xs text-muted-foreground">{t('start.draftHint')}</p>
            </div>
            <Button asChild size="sm"><Link to={wizardResumePath(draft.id)}>{t('start.continueCreate')} <ArrowRight className="h-3.5 w-3.5" /></Link></Button>
          </CardContent>
        </Card>
      )}

      {/* Tình trạng hiện tại */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatusTile label={t('start.tile.channels')} value={channels.length} hint={channels.length ? undefined : t('start.tile.noChannels')} />
        <StatusTile label={t('start.tile.bots')} value={bots.length} hint={bots.length ? t('start.tile.botsOn', { count: bots.filter((b) => b.enabled).length }) : t('start.tile.noBots')} />
        <StatusTile
          label={t('start.tile.answering')}
          value={`${answeringCount}/${channels.length}`}
          hint={paused ? t('start.tile.paused') : undefined}
          tone={paused ? 'warn' : answeringCount > 0 ? 'ok' : undefined}
        />
      </div>

      {channels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <PlugZap className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">{t('start.noChannels')}</p>
            <p className="max-w-md text-xs text-muted-foreground">
              {t('start.noChannelsHint')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {canConnect && <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="default" title={t('start.openConnectTitle')}><PlugZap className="h-4 w-4" /> {t('start.connect')}</OpenInNewTab>}
              <ReloadButton queryKeys={COVERAGE_KEYS} what={t('start.channelsWhat')} />
            </div>
            <p className="text-[11px] text-muted-foreground">{t('start.connectHint')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 space-y-1.5">
                <CardTitle className="flex items-center gap-2"><Bot className="h-4 w-4 text-primary" /> {t('start.coverage.title')}</CardTitle>
                <CardDescription>{t('start.coverage.desc')}</CardDescription>
              </div>
              <span className="flex flex-wrap items-center gap-1">
                <ReloadButton queryKeys={COVERAGE_KEYS} what={t('start.coverage.what')} />
                {canConnect && (
                  <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="outline" size="sm" title={t('start.openConnectTitle')}>
                    <PlugZap className="h-3.5 w-3.5" /> {t('start.connectMore')}
                  </OpenInNewTab>
                )}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="divide-y rounded-lg border">
              {coverage.map((c) => (
                <li key={c.channel.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <PlatformTag platform={c.channel.platform} />
                  <span className="min-w-0 flex-1 truncate font-medium">{c.channel.displayName || t('start.unnamedChannel')}</span>
                  {c.botId ? (
                    <Link to={botPath(c.botId)} className="text-xs text-primary hover:underline">
                      {c.botName}{c.viaDefault ? t('start.viaDefault') : ''}
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('start.noBot')}</span>
                  )}
                  {c.answering
                    ? <Badge variant="success">{t('start.answeringBadge')}</Badge>
                    : <Badge variant="outline">{t('start.notAnsweringBadge')}</Badge>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Việc tiếp theo */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 space-y-1.5">
              <CardTitle>{next.length ? t('start.next.title') : t('start.next.ready')}</CardTitle>
              <CardDescription>
                {next.length
                  ? t('start.next.progress', { done: done.length, total: steps.length })
                  : t('start.next.readyDesc')}
              </CardDescription>
            </div>
            <ReloadButton queryKeys={STEP_KEYS} what={t('start.next.what')} />
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {next.map((s) => <StepRow key={s.key} step={s} primary={s.key === current?.key} canEdit={canEdit} canConnect={canConnect} />)}
          {done.map((s) => <StepRow key={s.key} step={s} canEdit={canEdit} canConnect={canConnect} />)}
          {!next.length && (
            <OpenInNewTab to={`${AI_PATHS.tracking}#nhat-ky`} variant="outline" title={t('start.tracking.title')}>{t('start.tracking.open')}</OpenInNewTab>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatusTile({ label, value, hint, tone }: { label: string; value: number | string; hint?: string; tone?: 'ok' | 'warn' }) {
  return (
    <Card>
      <CardContent className="space-y-1 py-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn('text-2xl font-semibold tabular-nums', tone === 'ok' && 'text-success', tone === 'warn' && 'text-warning')}>{value}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  )
}

/** Việc cần quyền sửa AI (`ai.update`: tạo nhân viên, nguồn, bật trả lời) — người chỉ xem thì không hiện nút. */
const EDIT_STEPS = new Set<StartStep['key']>(['bot', 'assign', 'source', 'live'])

function StepRow({ step, primary, canEdit, canConnect }: { step: StartStep; primary?: boolean; canEdit: boolean; canConnect: boolean }) {
  const { t } = useTranslation('aiWizard')
  // Bước "Kết nối kênh" cần `integrations.create`; các bước sửa AI cần `ai.update`.
  const allowed = step.key === 'channel' ? canConnect : canEdit || !EDIT_STEPS.has(step.key)
  const showCta = !step.done && allowed
  return (
    <div className={cn('flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2.5', primary && 'border-primary/40 bg-primary/5', step.done && 'opacity-70')}>
      {step.done
        ? <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-label={t('start.step.done')} />
        : <Circle className="h-5 w-5 shrink-0 text-muted-foreground" aria-label={t('start.step.todo')} />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {step.title}
          {step.optional && !step.done && <span className="ml-2 text-xs font-normal text-muted-foreground">{t('start.step.recommended')}</span>}
        </p>
        <p className="text-xs text-muted-foreground">{step.desc}</p>
      </div>
      {showCta && (
        <AiFlowLink to={step.to} ctx={{ screen: 'hub' }} variant={primary ? 'default' : 'outline'} size="sm">{step.cta}</AiFlowLink>
      )}
    </div>
  )
}
